import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../services/api";
import { applyContactPhotoPatch } from "../../services/contactPhoto";
import useContactPhotoUpdates from "../../hooks/useContactPhotoUpdates";

export default function useContacts({ companyId, socket }) {
  const [query, setQuery] = useState({ searchParam: "", tags: [], pageNumber: 1, pageSize: 25 });
  const [search, setSearch] = useState("");
  const [result, setResult] = useState({ contacts: [], count: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const inFlight = useRef(false);
  const refreshPending = useRef(false);
  const sequence = useRef(0);
  const lastQuery = useRef(null);
  const mergePhotoSnapshot = useContactPhotoUpdates({ companyId, socket,
    onPatch: patch => setResult(current => applyContactPhotoPatch(current, patch)) });

  const refresh = useCallback(() => {
    if (inFlight.current) refreshPending.current = true;
    else setRevision(value => value + 1);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(current => current.searchParam === search.trim() ? current : {
        ...current, searchParam: search.trim(), pageNumber: 1
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const request = ++sequence.current;
    const source = axios.CancelToken.source();
    const queryKey = JSON.stringify([companyId, query]);
    if (lastQuery.current !== queryKey) {
      setResult({ contacts: [], count: 0, hasMore: false });
      lastQuery.current = queryKey;
    }
    inFlight.current = true;
    refreshPending.current = false;
    setLoading(true);
    setError(false);
    api.get("/contacts/", {
      params: {
        searchParam: query.searchParam, pageNumber: query.pageNumber,
        pageSize: query.pageSize, contactTag: JSON.stringify(query.tags)
      },
      timeout: 20000, cancelToken: source.token
    }).then(({ data }) => {
      if (request !== sequence.current) return;
      // Deletions can remove the last row of the last page while it is open.
      if (!data.contacts.length && query.pageNumber > 1) {
        setQuery(current => ({ ...current, pageNumber: current.pageNumber - 1 }));
        return;
      }
      setResult(current => ({ contacts: mergePhotoSnapshot(data.contacts, current.contacts), count: Number(data.count) || 0, hasMore: Boolean(data.hasMore) }));
    }).catch(err => {
      if (request === sequence.current && !axios.isCancel(err)) setError(true);
    }).finally(() => {
      if (request !== sequence.current) return;
      inFlight.current = false;
      setLoading(false);
      if (refreshPending.current) {
        refreshPending.current = false;
        setRevision(value => value + 1);
      }
    });
    return () => {
      sequence.current += 1;
      inFlight.current = false;
      source.cancel();
    };
  }, [companyId, query, revision, mergePhotoSnapshot]);

  useEffect(() => {
    if (!socket || !companyId) return undefined;
    let timer;
    const changed = data => {
      if (!["create", "update", "delete"].includes(data.action)) return;
      // Reload the current filtered page instead of inserting unordered socket rows.
      if (!timer) timer = setTimeout(() => { timer = null; refresh(); }, 600);
    };
    const event = `company-${companyId}-contact`;
    socket.on(event, changed);
    return () => { clearTimeout(timer); socket.off(event, changed); };
  }, [socket, companyId, refresh]);

  const setTags = useCallback(tags => setQuery(current => ({ ...current, tags, pageNumber: 1 })), []);
  const setPageSize = useCallback(pageSize => setQuery(current => ({ ...current, pageSize, pageNumber: 1 })), []);
  const setPage = useCallback(pageNumber => setQuery(current => ({ ...current, pageNumber })), []);

  return { ...result, ...query, search, setSearch, setTags, setPageSize, setPage, loading, error, refresh };
}
